import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import PremiumReportingApi from '../../api/PremiumReportingApi';
import { AuthData } from '../../context/auth';
import { ErrorData } from '../../context/ErrorProvider';
import { PolicyContext, PolicyData } from '../../context/policy';
import {
	PremiumReportData,
	withPremiumReportContext,
} from '../../context/premiumReport';
import { PremiumReportUpdateRequest } from '../../types/premiumReports';
import AuxDataView from '../auxData/AuxDataView';
import { StyledContentPane } from '../common';
import DisabledOverlay from '../DisabledOverlay';
import { useFastForm } from '../fastForm';
import LargeCenterSpinner from '../LargeCenterSpinner';
import LeftSidebarLayout from '../layouts/LeftSidebarLayout';
import PremiumReportLayout from '../layouts/PremiumReportLayout';
import PolicyInvoiceLayout from '../policy/PolicyInvoiceLayout';
import ProtectedRoute from '../ProtectedRoute';
import Documents from './Documents';
import InvoicesView from './InvoicesView';
import InvoiceView from './InvoiceView';
import PremiumReportSidebar from './PremiumReportSidebar';
import PriceBreakdown from './PriceBreakdown';
import ReportDetails from './ReportDetails';
import { getUpdateReqFromPremiumReportResponse } from './util';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	premiumReportData: PremiumReportData;
	errorData: ErrorData;
}

const PremiumReportingRoutes = (props: Props) => {
	const { policyData, authData, premiumReportData, errorData } = props;
	const {
		fields,
		setPremiumReport,
		setPremiumReports,
		setPremiumReportUpdateReq,
	} = premiumReportData;
	const authToken = authData.auth.authorizationToken;
	const { loadData } = policyData;
	const { handleErrorResponse } = errorData;
	const params = useParams();
	const navigate = useNavigate();
	const { policyId, premiumReportId } = params;
	const [loading, setLoading] = useState(true);
	const methods = useFastForm(fields, {});

	useEffect(() => {
		const fetchPremiumReport = async () => {
			setLoading(true);
			try {
				await loadData(policyId);
				// get premium report object
				const premiumReportResp = await PremiumReportingApi.getPremiumReport(
					authToken,
					premiumReportId,
				);
				const premiumReportsList = await PremiumReportingApi.getPremiumReports(
					authToken,
					premiumReportResp.data.policyLocator,
				);
				const premiumReportUpdateObj: PremiumReportUpdateRequest =
					getUpdateReqFromPremiumReportResponse(premiumReportResp.data);

				setPremiumReport(premiumReportResp.data);
				setPremiumReports(premiumReportsList.data);
				setPremiumReportUpdateReq(premiumReportUpdateObj);
			} catch (error) {
				handleErrorResponse(error, 'Failed to fetch premium report data');
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
			}
			setLoading(false);
		};

		fetchPremiumReport();
	}, [
		authData,
		authToken,
		handleErrorResponse,
		loadData,
		policyId,
		premiumReportId,
		setPremiumReport,
		setPremiumReportUpdateReq,
		setPremiumReports,
	]);

	if (loading) {
		return <LargeCenterSpinner />;
	}

	const renderMain = () => {
		return (
			<StyledContentPane>
				<PremiumReportLayout>
					<Routes>
						<Route path={`details`} element={<ReportDetails />} />
						<Route path={`documents`} element={<Documents />} />
						<Route
							path={`invoices/:invoiceId`}
							element={
								<ProtectedRoute permission={'invoice.fetch'}>
									<InvoiceView />
								</ProtectedRoute>
							}
						/>
						<Route
							path='invoices'
							element={
								<ProtectedRoute permission={'invoice.fetch'}>
									<PolicyInvoiceLayout />
								</ProtectedRoute>
							}
						>
							<Route index element={<InvoicesView />} />
						</Route>

						<Route path={`pricing`} element={<PriceBreakdown />} />
						<Route
							path={`auxData`}
							element={
								<ProtectedRoute permission='auxData.fetch'>
									<AuxDataView
										navigate={navigate}
										entityLocator={premiumReportId}
										entityType={'premiumReport'}
									/>
								</ProtectedRoute>
							}
						/>
						<Route path='/' element={<Navigate to={`details`} replace />} />
					</Routes>
				</PremiumReportLayout>
			</StyledContentPane>
		);
	};

	const renderSidebar = () => {
		if (loading || !premiumReportData.premiumReport) {
			return <DisabledOverlay />;
		}

		return <PremiumReportSidebar />;
	};

	return (
		<FormProvider {...methods}>
			<PolicyContext.Provider value={policyData}>
				<LeftSidebarLayout
					leftSidebarContent={renderSidebar()}
					mainContent={renderMain()}
				/>
			</PolicyContext.Provider>
		</FormProvider>
	);
};

export default withPremiumReportContext<Props>(PremiumReportingRoutes);
